import { Injectable } from '@angular/core';
import { setting } from '../../../../../../setting';
import { forkJoin, Observable, of } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { ArcaData } from 'services/billed-resquest/models/tax-authorities/argentina/arca.interface';
import { RestService } from 'app/shared/rest.service';

@Injectable()
export class ArcaService {
  private arcaData: ArcaData = {};
  private isInitialized = false;

  constructor(
    private _http: HttpClient,
    private _restService: RestService
  ) {}

  // Método principal para inicializar todos los datos
  initializeArcaData(): Promise<ArcaData> {
    if (this.isInitialized) {
      return Promise.resolve(this.arcaData);
    }

    return new Promise((resolve, reject) => {
      forkJoin({
        ivaConditions: this.getTypeIva().pipe(catchError(() => of([]))),
        receiverIVAConditions: this.getConditionIva().pipe(catchError(() => of([]))),
        currencies: this.getCurrencies().pipe(catchError(() => of([]))),
        ivaTypes: this.getIvaTypes().pipe(catchError(() => of([]))),
        tourismItems: this.getTourismItems().pipe(catchError(() => of([]))),
        enterprises: this.getEnterprise().pipe(catchError(() => of([]))),
        documentTypes: this.getDocType().pipe(catchError(() => of([]))),
        receiptTypes: this.getReceiptType().pipe(catchError(() => of([]))),
        conceptTypes: this.getConceptType().pipe(catchError(() => of([]))),
        invoiceCountries: this.countriesInvoiceT().pipe(catchError(() => of([]))),
        bookingDocumentTypes: this.getDoctTypeBookingList().pipe(catchError(() => of([]))),
        clients: this.getClients().pipe(catchError(() => of([]))),
        countries: this.getCountriesApi().pipe(catchError(() => of([]))),
        salesPoints: this.getSalespPointsApi().pipe(catchError(() => of([]))),
        salesPointsWsct: this.getSalespPointsApiWsct().pipe(catchError(() => of([]))),
      }).subscribe({
        next: (data) => {
          this.arcaData = data;
          this.isInitialized = true;
          console.log('ARCA Data initialized successfully');
          resolve(this.arcaData);
        },
        error: (error) => {
          console.error('Error initializing ARCA data:', error);
          reject(error);
        }
      });
    });
  }

  // Getters individuales para cada tipo de dato
  getTypeIva(): Observable<any> {
    if (this.arcaData.ivaConditions) {
      return of(this.arcaData.ivaConditions);
    }
    return this.makeArcaRequest('ivaConditions');
  }

  getConditionIva(): Observable<any> {
    if (this.arcaData.receiverIVAConditions) {
      return of(this.arcaData.receiverIVAConditions);
    }
    return this.makeArcaRequest('arca/catalogs/getReceiverIVACondition');
  }

  getCurrencies(): Observable<any> {
    if (this.arcaData.currencies) {
      return of(this.arcaData.currencies);
    }
    return this.makeArcaRequest('arca/catalogs/getTiposMonedas');
  }

  getIvaTypes(): Observable<any> {
    if (this.arcaData.ivaTypes) {
      return of(this.arcaData.ivaTypes);
    }
    return this.makeArcaRequest('arca/catalogs/getTiposIVA');
  }

  getTourismItems(): Observable<any> {
    if (this.arcaData.tourismItems) {
      return of(this.arcaData.tourismItems);
    }
    return this.makeArcaRequest('arca/wsct/catalogs/codigos-item-turismo');
  }

  getEnterprise(): Observable<any> {
    if (this.arcaData.enterprises) {
      return of(this.arcaData.enterprises);
    }
    return this.makeArcaRequest('enterprises');
  }

  getDocType(): Observable<any> {
    if (this.arcaData.documentTypes) {
      return of(this.arcaData.documentTypes);
    }
    return this.makeArcaRequest('arca/catalogs/getTiposDocumentos');
  }

  getReceiptType(): Observable<any> {
    if (this.arcaData.receiptTypes) {
      return of(this.arcaData.receiptTypes);
    }
    return this.makeArcaRequest('arca/catalogs/getTiposComprobantes');
  }

  getConceptType(): Observable<any> {
    if (this.arcaData.conceptTypes) {
      return of(this.arcaData.conceptTypes);
    }
    return this.makeArcaRequest('arca/catalogs/getTiposConcepto');
  }

  countriesInvoiceT(): Observable<any> {
    if (this.arcaData.invoiceCountries) {
      return of(this.arcaData.invoiceCountries);
    }
    return this.makeArcaRequest('arca/wsct/catalogs/paises');
  }

  // Métodos que usan RestService
  getDoctTypeBookingList(): Observable<any> {
    if (this.arcaData.bookingDocumentTypes) {
      return of(this.arcaData.bookingDocumentTypes);
    }
    return this._restService.doGet('app', 'documentTypes');
  }

  getClients(): Observable<any> {
    if (this.arcaData.clients) {
      return of(this.arcaData.clients);
    }
    return this._restService.doGet('app', 'clientsAllBilled');
  }

  getCountriesApi(): Observable<any> {
    if (this.arcaData.countries) {
      return of(this.arcaData.countries);
    }
    return this._restService.doGet('api', '/utilities/countries');
  }

  getDestinationsApi(country: string): Observable<any> {
    if (this.arcaData.destinations && this.arcaData.destinations[country]) {
      return of(this.arcaData.destinations[country]);
    }
    return this._restService.doGet('api', `/utilities/destinations/${country}`);
  }

  getCitiesApi(destination: number): Observable<any> {
    if (this.arcaData.cities && this.arcaData.cities[destination]) {
      return of(this.arcaData.cities[destination]);
    }
    return this._restService.doGet('api', `/utilities/cities/${destination}`);
  }

  getSalespPointsApi(): Observable<any> {
      if (this.arcaData.sales_point) {
      return of(this.arcaData.sales_point);
    }
    return this.makeArcaRequest('arca/catalogs/getPuntosDeVenta');
  }

    getSalespPointsApiWsct(): Observable<any> {
      if (this.arcaData.sales_point_wsct) {
      return of(this.arcaData.sales_point_wsct);
    }
    return this.makeArcaRequest('arca/catalogs/getPuntosDeVenta');
  }

  // Método auxiliar para hacer requests a ARCA
  private makeArcaRequest(endpoint: string): Observable<any> {
    const headers = new HttpHeaders()
      .set('ApiUser', setting.abacoApiUser)
      .set('apiKey', setting.abacoApiKey);

    const url = `${setting.apiAbacoUrl}${endpoint}`;

    return this._http.get<any>(url, { headers }).pipe(
      map(response => response.data || response),
      catchError(error => {
        console.error(`Error fetching ${endpoint}:`, error);
        return of([]);
      })
    );
  }

  // Método para obtener todos los datos cargados
  getArcaData(): ArcaData {
    return { ...this.arcaData };
  }

  // Método para verificar si está inicializado
  isDataInitialized(): boolean {
    return this.isInitialized;
  }

  // Método para resetear (útil para testing)
  reset(): void {
    this.arcaData = {};
    this.isInitialized = false;
  }

  // Método para cargar datos dinámicamente (destinos, ciudades)
  loadDestinations(country: string): Promise<any[]> {
    return new Promise((resolve, reject) => {
      this.getDestinationsApi(country).subscribe({
        next: (destinations) => {
          if (!this.arcaData.destinations) {
            this.arcaData.destinations = {};
          }
          this.arcaData.destinations[country] = destinations;
          resolve(destinations);
        },
        error: reject
      });
    });
  }

  loadCities(destination: number): Promise<any[]> {
    return new Promise((resolve, reject) => {
      this.getCitiesApi(destination).subscribe({
        next: (cities) => {
          if (!this.arcaData.cities) {
            this.arcaData.cities = {};
          }
          this.arcaData.cities[destination] = cities;
          resolve(cities);
        },
        error: reject
      });
    });
  }
}

